/**
 * PROM definitions (PROD-646 Sprint 3.3 + cardiometabolic / longevity
 * expansion 2026-05-07).
 *
 * The PROMs tab seeds 13 instruments across the cardiometabolic and
 * longevity corpora:
 *   Existing 3 (Sprint 3.3): PHQ-9, GAD-7, MSQ baseline.
 *   Cardiometabolic 6 (this expansion): SF-12, GSRS, IWQOL-Lite, DQOL,
 *     EQ-5D-5L, KOOS-12.
 *   Longevity 4 (this expansion): PROMIS-29 v2.1, WHO-5, electronic
 *     Frailty Index (eFI), Healthy Aging Index.
 *
 * Per HC3 every definition carries a `citation` and a `loincCode`
 * (canonical LOINC where one exists, placeholder + `advisoryFlag` where
 * clinical advisory must still confirm).
 *
 * Sources of truth for the LOINC codes (HC3):
 *   - PHQ-9 panel        -> LOINC 44249-1 (Patient Health Questionnaire
 *     9 item panel). Severity bands per Kroenke, Spitzer, Williams
 *     (2001) JGIM 16(9): 606-613.
 *   - GAD-7              -> LOINC 69737-5 (Generalized anxiety disorder
 *     7 item panel total score). Severity bands per Spitzer, Kroenke,
 *     Williams, Lowe (2006) Arch Intern Med 166(10): 1092-1097.
 *   - MSQ baseline       -> LOINC LA31978-5 placeholder. Tracked as
 *     MSQ-LOINC-CONFIRM in docs/SIGNAL-DS-CONTRIBUTIONS.md gap log.
 *   - SF-12              -> LOINC 71956-7 (SF-12 v1 short form). Bands
 *     per Ware, Kosinski, Keller (1996) Med Care 34(3): 220-233.
 *   - GSRS               -> LOINC LA-CPS-GSRS placeholder (advisory).
 *     Bands per Svedlund, Sjodin, Dotevall (1988) Dig Dis Sci 33(2).
 *   - IWQOL-Lite         -> LOINC LA-CPS-IWQOL placeholder (advisory).
 *     Reference: Kolotkin, Crosby, Kosloski, Williams (2001) Obes Res
 *     9(2): 102-111.
 *   - DQOL               -> LOINC LA-CPS-DQOL placeholder (advisory).
 *     Reference: DCCT Research Group (1988) Diabetes Care 11(9).
 *   - EQ-5D-5L           -> LOINC 89257-9 (EQ-5D-5L total score).
 *     Reference: Herdman et al (2011) Qual Life Res 20(10).
 *   - KOOS-12            -> LOINC LA-CPS-KOOS12 placeholder (advisory).
 *     Reference: Gandek, Roos, Franklin, Ware (2019) Osteoarthr Cart
 *     27(5): 754-761.
 *   - PROMIS-29 v2.1     -> LOINC 82589-3 (PROMIS-29 v2.0 anchor; v2.1
 *     uses identical raw-score map). Reference: Cella et al (2010)
 *     J Clin Epidemiol 63(11): 1179-1194.
 *   - WHO-5 Well-Being   -> LOINC 89260-3 (WHO-5 well-being index total
 *     score). Reference: Topp, Ostergaard, Sondergaard, Bech (2015)
 *     Psychother Psychosom 84(3): 167-176.
 *   - eFI                -> LOINC LA-CPS-EFI placeholder (advisory).
 *     Reference: Clegg, Bates, Young, Ryan, et al (2016) Age Ageing
 *     45(3): 353-360.
 *   - Healthy Aging Idx  -> LOINC LA-CPS-HAI placeholder (advisory).
 *     Reference: Sanders, Boudreau, Cauley, et al (2014) J Gerontol A
 *     Biol Sci Med Sci 69(2): 187-194.
 *
 * HC1: every definition is byte-stable. No clocks, no randomness.
 * HC2: tokens-only - definitions carry no styling, only data.
 * HC3: each PROM exposes a `citation` field naming the validated source.
 * HC5: zero em / en dashes anywhere.
 * HC9: definitions are static; emission of the AuditEvent + PROM submission
 *      lives in PromCard with `source=proms-tab`.
 */

/**
 * Closed enum of supported PROM ids. Sprint 3.3 shipped the first three;
 * the 2026-05-07 expansion adds six cardiometabolic instruments + four
 * longevity instruments for a total of 13 selectable in the engine.
 */
export type PromId =
  | 'phq-9'
  | 'gad-7'
  | 'msq-baseline'
  // Cardiometabolic expansion.
  | 'sf-12'
  | 'gsrs'
  | 'iwqol-lite'
  | 'dqol'
  | 'eq-5d-5l'
  | 'koos-12'
  // Longevity expansion.
  | 'promis-29'
  | 'who-5'
  | 'efi'
  | 'healthy-aging-index';

/**
 * Closed enum of PROM corpus tracks. Drives the right-rail Instrument
 * Plan grouping and the AdminSchedulePanel pathway-vs-longevity split.
 */
export type PromTrack = 'cardiometabolic' | 'longevity';

/** A single Likert option presented for an item. */
export interface PromLikertOption {
  /** Display label (verbatim). */
  label: string;
  /** Numeric value contributing to the score. */
  value: number;
}

/** A single PROM question. */
export interface PromQuestionDef {
  /** Stable item id, e.g. `phq-9-q1`. */
  linkId: string;
  /** Verbatim question text. */
  text: string;
  /** Likert options in display order. */
  options: ReadonlyArray<PromLikertOption>;
}

/** Score band for the interpretation table. */
export interface PromScoreBand {
  /** Inclusive lower bound. */
  min: number;
  /** Inclusive upper bound. */
  max: number;
  /** Verbatim severity label, e.g. `Moderate depression`. */
  label: string;
}

/** Full PROM definition including scoring + provenance. */
export interface PromDefinition {
  id: PromId;
  /** Card title (verbatim). */
  title: string;
  /** Single-sentence card description. */
  description: string;
  /** Canonical questionnaire URL used in the QuestionnaireResponse. */
  questionnaireUrl: string;
  /** LOINC code applied to the derived Observation. */
  loincCode: string;
  /** LOINC display label for the Observation.code. */
  loincDisplay: string;
  /** Optional unit of measure for the score. */
  scoreUnit?: string;
  /** Citation string surfaced in the card per HC3. */
  citation: string;
  /** Optional clinical-advisory flag (MSQ until LOINC is confirmed). */
  advisoryFlag?: string;
  /** Question list. */
  questions: ReadonlyArray<PromQuestionDef>;
  /** Score interpretation bands in ascending order. */
  scoreBands: ReadonlyArray<PromScoreBand>;
  /**
   * Corpus track. Drives the right-rail Instrument Plan grouping and the
   * Longevity Profile toggle in the AdminSchedulePanel.
   */
  track: PromTrack;
  /**
   * Total item count in the validated instrument as cataloged in the
   * Scrips PROM Engine (e.g. MSQ baseline maps to the 59-item parent
   * questionnaire). Surface used by the Instrument Plan sidebar; the
   * card itself renders only `questions.length` items in Demo Mode.
   */
  itemCountTotal: number;
  /**
   * License posture. `free` = open instrument or commonly-licensed under
   * a permissive academic re-use clause. `scrips-ip` = re-administered
   * variant authored by Scrips (advisory flag where applicable).
   * `licensed` = third-party requires individual seat license. Surfaced
   * verbatim in the right-rail plan list per the prototype IP markers.
   */
  licenseStatus: 'free' | 'scrips-ip' | 'licensed';
}

// --- PHQ-9 ------------------------------------------------------------------

const PHQ9_LIKERT: ReadonlyArray<PromLikertOption> = [
  { label: 'Not at all', value: 0 },
  { label: 'Several days', value: 1 },
  { label: 'More than half the days', value: 2 },
  { label: 'Nearly every day', value: 3 },
];

/**
 * PHQ-9 items (verbatim per the validated instrument; Kroenke, Spitzer,
 * Williams 2001).
 */
const PHQ9_QUESTIONS: ReadonlyArray<PromQuestionDef> = [
  {
    linkId: 'phq-9-q1',
    text: 'Little interest or pleasure in doing things',
    options: PHQ9_LIKERT,
  },
  {
    linkId: 'phq-9-q2',
    text: 'Feeling down, depressed, or hopeless',
    options: PHQ9_LIKERT,
  },
  {
    linkId: 'phq-9-q3',
    text: 'Trouble falling or staying asleep, or sleeping too much',
    options: PHQ9_LIKERT,
  },
  {
    linkId: 'phq-9-q4',
    text: 'Feeling tired or having little energy',
    options: PHQ9_LIKERT,
  },
  {
    linkId: 'phq-9-q5',
    text: 'Poor appetite or overeating',
    options: PHQ9_LIKERT,
  },
  {
    linkId: 'phq-9-q6',
    text: 'Feeling bad about yourself, or that you are a failure, or have let yourself or your family down',
    options: PHQ9_LIKERT,
  },
  {
    linkId: 'phq-9-q7',
    text: 'Trouble concentrating on things, such as reading the newspaper or watching television',
    options: PHQ9_LIKERT,
  },
  {
    linkId: 'phq-9-q8',
    text: 'Moving or speaking so slowly that other people could have noticed, or the opposite, being so fidgety or restless that you have been moving around a lot more than usual',
    options: PHQ9_LIKERT,
  },
  {
    linkId: 'phq-9-q9',
    text: 'Thoughts that you would be better off dead or of hurting yourself in some way',
    options: PHQ9_LIKERT,
  },
];

/** PHQ-9 severity bands per Kroenke, Spitzer, Williams (2001) JGIM 16(9). */
const PHQ9_BANDS: ReadonlyArray<PromScoreBand> = [
  { min: 0, max: 4, label: 'Minimal depression' },
  { min: 5, max: 9, label: 'Mild depression' },
  { min: 10, max: 14, label: 'Moderate depression' },
  { min: 15, max: 19, label: 'Moderately severe depression' },
  { min: 20, max: 27, label: 'Severe depression' },
];

export const PHQ9_DEFINITION: PromDefinition = {
  id: 'phq-9',
  title: 'PHQ-9 Depression',
  description:
    'Patient Health Questionnaire 9 item depression screen. Sum the 9 items, score 0 to 27.',
  questionnaireUrl: 'urn:cps:questionnaire:phq-9',
  loincCode: '44249-1',
  loincDisplay: 'PHQ-9 quick depression assessment panel',
  scoreUnit: 'score',
  citation:
    'Kroenke K, Spitzer RL, Williams JBW. The PHQ-9: validity of a brief depression severity measure. J Gen Intern Med 2001;16(9):606-613. LOINC 44249-1.',
  questions: PHQ9_QUESTIONS,
  scoreBands: PHQ9_BANDS,
  track: 'cardiometabolic',
  itemCountTotal: 9,
  licenseStatus: 'free',
};

// --- GAD-7 ------------------------------------------------------------------

const GAD7_LIKERT: ReadonlyArray<PromLikertOption> = [
  { label: 'Not at all', value: 0 },
  { label: 'Several days', value: 1 },
  { label: 'More than half the days', value: 2 },
  { label: 'Nearly every day', value: 3 },
];

/**
 * GAD-7 items (verbatim per Spitzer, Kroenke, Williams, Lowe 2006).
 */
const GAD7_QUESTIONS: ReadonlyArray<PromQuestionDef> = [
  {
    linkId: 'gad-7-q1',
    text: 'Feeling nervous, anxious, or on edge',
    options: GAD7_LIKERT,
  },
  {
    linkId: 'gad-7-q2',
    text: 'Not being able to stop or control worrying',
    options: GAD7_LIKERT,
  },
  {
    linkId: 'gad-7-q3',
    text: 'Worrying too much about different things',
    options: GAD7_LIKERT,
  },
  {
    linkId: 'gad-7-q4',
    text: 'Trouble relaxing',
    options: GAD7_LIKERT,
  },
  {
    linkId: 'gad-7-q5',
    text: 'Being so restless that it is hard to sit still',
    options: GAD7_LIKERT,
  },
  {
    linkId: 'gad-7-q6',
    text: 'Becoming easily annoyed or irritable',
    options: GAD7_LIKERT,
  },
  {
    linkId: 'gad-7-q7',
    text: 'Feeling afraid as if something awful might happen',
    options: GAD7_LIKERT,
  },
];

/** GAD-7 severity bands per Spitzer et al (2006). */
const GAD7_BANDS: ReadonlyArray<PromScoreBand> = [
  { min: 0, max: 4, label: 'Minimal anxiety' },
  { min: 5, max: 9, label: 'Mild anxiety' },
  { min: 10, max: 14, label: 'Moderate anxiety' },
  { min: 15, max: 21, label: 'Severe anxiety' },
];

export const GAD7_DEFINITION: PromDefinition = {
  id: 'gad-7',
  title: 'GAD-7 Anxiety',
  description:
    'Generalized Anxiety Disorder 7 item screen. Sum the 7 items, score 0 to 21.',
  questionnaireUrl: 'urn:cps:questionnaire:gad-7',
  loincCode: '69737-5',
  loincDisplay: 'Generalized anxiety disorder 7 item (GAD-7) total score',
  scoreUnit: 'score',
  citation:
    'Spitzer RL, Kroenke K, Williams JBW, Lowe B. A brief measure for assessing generalized anxiety disorder. Arch Intern Med 2006;166(10):1092-1097. LOINC 69737-5.',
  questions: GAD7_QUESTIONS,
  scoreBands: GAD7_BANDS,
  track: 'cardiometabolic',
  itemCountTotal: 7,
  licenseStatus: 'free',
};

// --- MSQ baseline -----------------------------------------------------------

const MSQ_LIKERT: ReadonlyArray<PromLikertOption> = [
  { label: 'Not present', value: 0 },
  { label: 'Mild', value: 1 },
  { label: 'Moderate', value: 2 },
  { label: 'Severe', value: 3 },
];

/**
 * MSQ baseline items - menopause symptom subscale subset used by the
 * cardiometabolic / longevity corpus PROM block per master prompt v3.3
 * §4.3 Phase 2. Six items, sum 0 to 18.
 */
const MSQ_QUESTIONS: ReadonlyArray<PromQuestionDef> = [
  {
    linkId: 'msq-q1',
    text: 'Hot flashes',
    options: MSQ_LIKERT,
  },
  {
    linkId: 'msq-q2',
    text: 'Night sweats',
    options: MSQ_LIKERT,
  },
  {
    linkId: 'msq-q3',
    text: 'Sleep disturbance',
    options: MSQ_LIKERT,
  },
  {
    linkId: 'msq-q4',
    text: 'Mood changes',
    options: MSQ_LIKERT,
  },
  {
    linkId: 'msq-q5',
    text: 'Joint or muscle aches',
    options: MSQ_LIKERT,
  },
  {
    linkId: 'msq-q6',
    text: 'Vaginal dryness or discomfort',
    options: MSQ_LIKERT,
  },
];

/**
 * MSQ baseline severity bands - clinical-advisory placeholder bands
 * derived from the 0-18 sum range. Tracked as MSQ-LOINC-CONFIRM in the
 * gap log; clinical advisory must confirm canonical bands before this
 * escapes Demo Mode.
 */
const MSQ_BANDS: ReadonlyArray<PromScoreBand> = [
  { min: 0, max: 5, label: 'Minimal symptom burden' },
  { min: 6, max: 11, label: 'Moderate symptom burden' },
  { min: 12, max: 18, label: 'High symptom burden' },
];

export const MSQ_DEFINITION: PromDefinition = {
  id: 'msq-baseline',
  title: 'MSQ Baseline',
  description:
    'Menopause Specific Questionnaire baseline symptom block. Sum the 6 items, score 0 to 18.',
  questionnaireUrl: 'urn:cps:questionnaire:msq-baseline',
  loincCode: 'LA31978-5',
  loincDisplay: 'MSQ baseline total score (placeholder)',
  scoreUnit: 'score',
  citation:
    'Hilditch JR, Lewis J, Peter A, et al. A menopause-specific quality of life questionnaire: development and psychometric properties. Maturitas 1996;24(3):161-175.',
  advisoryFlag:
    'LOINC LA31978-5 is a placeholder until clinical advisory confirms a canonical menopause / longevity LOINC.',
  questions: MSQ_QUESTIONS,
  scoreBands: MSQ_BANDS,
  track: 'cardiometabolic',
  itemCountTotal: 59,
  licenseStatus: 'scrips-ip',
};

// --- SF-12 (Short Form 12) --------------------------------------------------

const SF12_LIKERT_5: ReadonlyArray<PromLikertOption> = [
  { label: 'Excellent', value: 5 },
  { label: 'Very good', value: 4 },
  { label: 'Good', value: 3 },
  { label: 'Fair', value: 2 },
  { label: 'Poor', value: 1 },
];

const SF12_LIKERT_3: ReadonlyArray<PromLikertOption> = [
  { label: 'Yes, limited a lot', value: 1 },
  { label: 'Yes, limited a little', value: 2 },
  { label: 'No, not limited', value: 3 },
];

const SF12_LIKERT_YES_NO: ReadonlyArray<PromLikertOption> = [
  { label: 'Yes, all of the time', value: 1 },
  { label: 'Most of the time', value: 2 },
  { label: 'Some of the time', value: 3 },
  { label: 'A little of the time', value: 4 },
  { label: 'None of the time', value: 5 },
];

/**
 * SF-12 short form (12-item subset of SF-36). Sprint expansion uses a
 * 6-item Demo-mode core spanning the General Health, Physical Function,
 * Role-Physical, Bodily Pain, Vitality, and Mental Health domains. The
 * full 12-item battery is referenced through `itemCountTotal`; clinical
 * advisory must confirm the SF-12 v1 vs v2 license posture before this
 * card escapes Demo Mode.
 */
const SF12_QUESTIONS: ReadonlyArray<PromQuestionDef> = [
  {
    linkId: 'sf-12-q1',
    text: 'In general, would you say your health is',
    options: SF12_LIKERT_5,
  },
  {
    linkId: 'sf-12-q2',
    text: 'Does your health limit you in moderate activities such as moving a table, vacuuming, or playing golf',
    options: SF12_LIKERT_3,
  },
  {
    linkId: 'sf-12-q3',
    text: 'Does your health limit you in climbing several flights of stairs',
    options: SF12_LIKERT_3,
  },
  {
    linkId: 'sf-12-q4',
    text: 'During the past 4 weeks, how much did pain interfere with your normal work',
    options: SF12_LIKERT_5,
  },
  {
    linkId: 'sf-12-q5',
    text: 'During the past 4 weeks, how much of the time did you have a lot of energy',
    options: SF12_LIKERT_YES_NO,
  },
  {
    linkId: 'sf-12-q6',
    text: 'During the past 4 weeks, how much of the time have you felt calm and peaceful',
    options: SF12_LIKERT_YES_NO,
  },
];

/**
 * SF-12 demo bands across the 6 to 30 demo-sum range. Production scoring
 * normalizes onto the PCS / MCS T-score (mean 50, SD 10); the demo band
 * mapping below preserves the directional interpretation (higher is
 * better). Tracked as SF-12-PCS-MCS-CONFIRM in the gap log.
 */
const SF12_BANDS: ReadonlyArray<PromScoreBand> = [
  { min: 6, max: 14, label: 'Below population norm' },
  { min: 15, max: 22, label: 'Near population norm' },
  { min: 23, max: 30, label: 'Above population norm' },
];

export const SF12_DEFINITION: PromDefinition = {
  id: 'sf-12',
  title: 'SF-12 Health Status',
  description:
    'Short Form 12 general health status, sampling the Physical and Mental Component Summary domains. Sum the 6 demo items, score 6 to 30.',
  questionnaireUrl: 'urn:cps:questionnaire:sf-12',
  loincCode: '71956-7',
  loincDisplay: 'SF-12 v1 short form 12 panel',
  scoreUnit: 'score',
  citation:
    'Ware J, Kosinski M, Keller SD. A 12-Item Short-Form Health Survey: construction of scales and preliminary tests of reliability and validity. Med Care 1996;34(3):220-233. LOINC 71956-7.',
  advisoryFlag:
    'Demo bands map to a 6 to 30 sum; production scoring uses the SF-12 PCS / MCS T-scores. Gap log: SF-12-PCS-MCS-CONFIRM.',
  questions: SF12_QUESTIONS,
  scoreBands: SF12_BANDS,
  track: 'cardiometabolic',
  itemCountTotal: 12,
  licenseStatus: 'licensed',
};

// --- GSRS (Gastrointestinal Symptom Rating Scale) ---------------------------

const GSRS_LIKERT: ReadonlyArray<PromLikertOption> = [
  { label: 'No discomfort', value: 1 },
  { label: 'Minor', value: 2 },
  { label: 'Mild', value: 3 },
  { label: 'Moderate', value: 4 },
  { label: 'Moderately severe', value: 5 },
  { label: 'Severe', value: 6 },
  { label: 'Very severe', value: 7 },
];

/**
 * GSRS 6-item demo subset (one item per Svedlund 5-cluster, plus an
 * overall global symptom). Loaded for GLP-1 side-effect monitoring.
 */
const GSRS_QUESTIONS: ReadonlyArray<PromQuestionDef> = [
  {
    linkId: 'gsrs-q1',
    text: 'Abdominal pain in the past week',
    options: GSRS_LIKERT,
  },
  {
    linkId: 'gsrs-q2',
    text: 'Heartburn or acid reflux in the past week',
    options: GSRS_LIKERT,
  },
  {
    linkId: 'gsrs-q3',
    text: 'Nausea or vomiting in the past week',
    options: GSRS_LIKERT,
  },
  {
    linkId: 'gsrs-q4',
    text: 'Bloating or abdominal distension in the past week',
    options: GSRS_LIKERT,
  },
  {
    linkId: 'gsrs-q5',
    text: 'Constipation in the past week',
    options: GSRS_LIKERT,
  },
  {
    linkId: 'gsrs-q6',
    text: 'Diarrhea in the past week',
    options: GSRS_LIKERT,
  },
];

const GSRS_BANDS: ReadonlyArray<PromScoreBand> = [
  { min: 6, max: 12, label: 'Mild GI symptom burden' },
  { min: 13, max: 24, label: 'Moderate GI symptom burden' },
  { min: 25, max: 42, label: 'High GI symptom burden' },
];

export const GSRS_DEFINITION: PromDefinition = {
  id: 'gsrs',
  title: 'GSRS GI Symptoms',
  description:
    'Gastrointestinal Symptom Rating Scale demo subset. Relevant for GLP-1 side-effect monitoring. Sum the 6 demo items, score 6 to 42.',
  questionnaireUrl: 'urn:cps:questionnaire:gsrs',
  loincCode: 'LA-CPS-GSRS',
  loincDisplay: 'GSRS demo subset total score (placeholder)',
  scoreUnit: 'score',
  citation:
    'Svedlund J, Sjodin I, Dotevall G. GSRS: a clinical rating scale for gastrointestinal symptoms in patients with irritable bowel syndrome and peptic ulcer disease. Dig Dis Sci 1988;33(2):129-134.',
  advisoryFlag:
    'LOINC LA-CPS-GSRS is a placeholder until clinical advisory confirms a canonical GSRS LOINC. Tracked as GSRS-LOINC-CONFIRM.',
  questions: GSRS_QUESTIONS,
  scoreBands: GSRS_BANDS,
  track: 'cardiometabolic',
  itemCountTotal: 15,
  licenseStatus: 'licensed',
};

// --- IWQOL-Lite (Impact of Weight on Quality of Life) -----------------------

const IWQOL_LIKERT: ReadonlyArray<PromLikertOption> = [
  { label: 'Never true', value: 1 },
  { label: 'Rarely true', value: 2 },
  { label: 'Sometimes true', value: 3 },
  { label: 'Usually true', value: 4 },
  { label: 'Always true', value: 5 },
];

const IWQOL_QUESTIONS: ReadonlyArray<PromQuestionDef> = [
  {
    linkId: 'iwqol-q1',
    text: 'Because of my weight I have trouble picking up objects',
    options: IWQOL_LIKERT,
  },
  {
    linkId: 'iwqol-q2',
    text: 'Because of my weight I get short of breath with moderate exertion',
    options: IWQOL_LIKERT,
  },
  {
    linkId: 'iwqol-q3',
    text: 'Because of my weight I have low self-esteem',
    options: IWQOL_LIKERT,
  },
  {
    linkId: 'iwqol-q4',
    text: 'Because of my weight I am self-conscious',
    options: IWQOL_LIKERT,
  },
  {
    linkId: 'iwqol-q5',
    text: 'Because of my weight I am uncomfortable in public seating',
    options: IWQOL_LIKERT,
  },
  {
    linkId: 'iwqol-q6',
    text: 'Because of my weight I worry about my future health',
    options: IWQOL_LIKERT,
  },
];

const IWQOL_BANDS: ReadonlyArray<PromScoreBand> = [
  { min: 6, max: 12, label: 'Minimal weight-related impact' },
  { min: 13, max: 22, label: 'Moderate weight-related impact' },
  { min: 23, max: 30, label: 'Severe weight-related impact' },
];

export const IWQOL_DEFINITION: PromDefinition = {
  id: 'iwqol-lite',
  title: 'IWQOL-Lite Weight QoL',
  description:
    'Impact of Weight on Quality of Life Lite. Demo subset, 6 items across the physical and self-esteem subscales. Sum, score 6 to 30.',
  questionnaireUrl: 'urn:cps:questionnaire:iwqol-lite',
  loincCode: 'LA-CPS-IWQOL',
  loincDisplay: 'IWQOL-Lite demo subset total score (placeholder)',
  scoreUnit: 'score',
  citation:
    'Kolotkin RL, Crosby RD, Kosloski KD, Williams GR. Development of a brief measure to assess quality of life in obesity. Obes Res 2001;9(2):102-111.',
  advisoryFlag:
    'LOINC LA-CPS-IWQOL is a placeholder until clinical advisory confirms a canonical IWQOL-Lite LOINC. Tracked as IWQOL-LOINC-CONFIRM.',
  questions: IWQOL_QUESTIONS,
  scoreBands: IWQOL_BANDS,
  track: 'cardiometabolic',
  itemCountTotal: 31,
  licenseStatus: 'licensed',
};

// --- DQOL (Diabetes Quality of Life) ----------------------------------------

const DQOL_LIKERT: ReadonlyArray<PromLikertOption> = [
  { label: 'Very satisfied', value: 1 },
  { label: 'Moderately satisfied', value: 2 },
  { label: 'Neither', value: 3 },
  { label: 'Moderately dissatisfied', value: 4 },
  { label: 'Very dissatisfied', value: 5 },
];

const DQOL_QUESTIONS: ReadonlyArray<PromQuestionDef> = [
  {
    linkId: 'dqol-q1',
    text: 'How satisfied are you with the amount of time it takes to manage your diabetes',
    options: DQOL_LIKERT,
  },
  {
    linkId: 'dqol-q2',
    text: 'How satisfied are you with the time you spend getting checkups',
    options: DQOL_LIKERT,
  },
  {
    linkId: 'dqol-q3',
    text: 'How satisfied are you with your current treatment regimen',
    options: DQOL_LIKERT,
  },
  {
    linkId: 'dqol-q4',
    text: 'How satisfied are you with the flexibility you have in your diet',
    options: DQOL_LIKERT,
  },
  {
    linkId: 'dqol-q5',
    text: 'How satisfied are you with your knowledge about diabetes',
    options: DQOL_LIKERT,
  },
  {
    linkId: 'dqol-q6',
    text: 'How satisfied are you with your sleep',
    options: DQOL_LIKERT,
  },
];

const DQOL_BANDS: ReadonlyArray<PromScoreBand> = [
  { min: 6, max: 12, label: 'High diabetes life satisfaction' },
  { min: 13, max: 22, label: 'Moderate diabetes life satisfaction' },
  { min: 23, max: 30, label: 'Low diabetes life satisfaction' },
];

export const DQOL_DEFINITION: PromDefinition = {
  id: 'dqol',
  title: 'DQOL Diabetes QoL',
  description:
    'Diabetes Quality of Life satisfaction subscale demo subset. Sum the 6 demo items, score 6 to 30. Lower score is higher satisfaction.',
  questionnaireUrl: 'urn:cps:questionnaire:dqol',
  loincCode: 'LA-CPS-DQOL',
  loincDisplay: 'DQOL satisfaction subscale total score (placeholder)',
  scoreUnit: 'score',
  citation:
    'DCCT Research Group. Reliability and validity of a diabetes quality-of-life measure for the Diabetes Control and Complications Trial. Diabetes Care 1988;11(9):725-732.',
  advisoryFlag:
    'LOINC LA-CPS-DQOL is a placeholder until clinical advisory confirms a canonical DQOL LOINC. Tracked as DQOL-LOINC-CONFIRM.',
  questions: DQOL_QUESTIONS,
  scoreBands: DQOL_BANDS,
  track: 'cardiometabolic',
  itemCountTotal: 52,
  licenseStatus: 'free',
};

// --- EQ-5D-5L (EuroQol 5 Dimension 5 Level) --------------------------------

const EQ5D_LIKERT: ReadonlyArray<PromLikertOption> = [
  { label: 'No problems', value: 1 },
  { label: 'Slight problems', value: 2 },
  { label: 'Moderate problems', value: 3 },
  { label: 'Severe problems', value: 4 },
  { label: 'Unable / extreme', value: 5 },
];

const EQ5D_QUESTIONS: ReadonlyArray<PromQuestionDef> = [
  {
    linkId: 'eq-5d-q1',
    text: 'Mobility today',
    options: EQ5D_LIKERT,
  },
  {
    linkId: 'eq-5d-q2',
    text: 'Self-care today',
    options: EQ5D_LIKERT,
  },
  {
    linkId: 'eq-5d-q3',
    text: 'Usual activities today (work, study, family, leisure)',
    options: EQ5D_LIKERT,
  },
  {
    linkId: 'eq-5d-q4',
    text: 'Pain or discomfort today',
    options: EQ5D_LIKERT,
  },
  {
    linkId: 'eq-5d-q5',
    text: 'Anxiety or depression today',
    options: EQ5D_LIKERT,
  },
];

const EQ5D_BANDS: ReadonlyArray<PromScoreBand> = [
  { min: 5, max: 9, label: 'Minimal HRQOL impairment' },
  { min: 10, max: 17, label: 'Moderate HRQOL impairment' },
  { min: 18, max: 25, label: 'Severe HRQOL impairment' },
];

export const EQ5D_DEFINITION: PromDefinition = {
  id: 'eq-5d-5l',
  title: 'EQ-5D-5L HRQOL',
  description:
    'EuroQol 5-Dimension 5-Level generic health status. 5 items across mobility, self-care, activity, pain, and mood. Sum, score 5 to 25.',
  questionnaireUrl: 'urn:cps:questionnaire:eq-5d-5l',
  loincCode: '89257-9',
  loincDisplay: 'EQ-5D-5L total score',
  scoreUnit: 'score',
  citation:
    'Herdman M, Gudex C, Lloyd A, et al. Development and preliminary testing of the new five-level version of EQ-5D (EQ-5D-5L). Qual Life Res 2011;20(10):1727-1736. LOINC 89257-9.',
  questions: EQ5D_QUESTIONS,
  scoreBands: EQ5D_BANDS,
  track: 'cardiometabolic',
  itemCountTotal: 5,
  licenseStatus: 'licensed',
};

// --- KOOS-12 (Knee injury and Osteoarthritis Outcome Score) ----------------

const KOOS_LIKERT: ReadonlyArray<PromLikertOption> = [
  { label: 'None', value: 0 },
  { label: 'Mild', value: 1 },
  { label: 'Moderate', value: 2 },
  { label: 'Severe', value: 3 },
  { label: 'Extreme', value: 4 },
];

const KOOS_QUESTIONS: ReadonlyArray<PromQuestionDef> = [
  {
    linkId: 'koos-q1',
    text: 'Pain in your knee while walking on a flat surface',
    options: KOOS_LIKERT,
  },
  {
    linkId: 'koos-q2',
    text: 'Pain in your knee while going up or down stairs',
    options: KOOS_LIKERT,
  },
  {
    linkId: 'koos-q3',
    text: 'Stiffness in your knee after sitting',
    options: KOOS_LIKERT,
  },
  {
    linkId: 'koos-q4',
    text: 'Difficulty rising from a chair',
    options: KOOS_LIKERT,
  },
  {
    linkId: 'koos-q5',
    text: 'Difficulty squatting',
    options: KOOS_LIKERT,
  },
  {
    linkId: 'koos-q6',
    text: 'Trouble enjoying recreational activities because of your knee',
    options: KOOS_LIKERT,
  },
];

const KOOS_BANDS: ReadonlyArray<PromScoreBand> = [
  { min: 0, max: 6, label: 'Minimal knee impact' },
  { min: 7, max: 14, label: 'Moderate knee impact' },
  { min: 15, max: 24, label: 'Severe knee impact' },
];

export const KOOS_DEFINITION: PromDefinition = {
  id: 'koos-12',
  title: 'KOOS-12 Knee Function',
  description:
    'Knee injury and Osteoarthritis Outcome Score short form. 6-item demo subset across pain, stiffness, function, and recreation. Sum, score 0 to 24.',
  questionnaireUrl: 'urn:cps:questionnaire:koos-12',
  loincCode: 'LA-CPS-KOOS12',
  loincDisplay: 'KOOS-12 demo subset total score (placeholder)',
  scoreUnit: 'score',
  citation:
    'Gandek B, Roos EM, Franklin PD, Ware JE. A 12-item short form of the Knee injury and Osteoarthritis Outcome Score (KOOS-12). Osteoarthr Cart 2019;27(5):754-761.',
  advisoryFlag:
    'LOINC LA-CPS-KOOS12 is a placeholder until clinical advisory confirms a canonical KOOS-12 LOINC. Tracked as KOOS-LOINC-CONFIRM.',
  questions: KOOS_QUESTIONS,
  scoreBands: KOOS_BANDS,
  track: 'cardiometabolic',
  itemCountTotal: 12,
  licenseStatus: 'free',
};

// --- PROMIS-29 v2.1 ---------------------------------------------------------

const PROMIS29_LIKERT_5: ReadonlyArray<PromLikertOption> = [
  { label: 'Never', value: 1 },
  { label: 'Rarely', value: 2 },
  { label: 'Sometimes', value: 3 },
  { label: 'Often', value: 4 },
  { label: 'Always', value: 5 },
];

/**
 * PROMIS-29 v2.1 demo subset spanning 7 NIH PROMIS domains. Production
 * scoring transforms each domain raw score onto a T-score (mean 50, SD
 * 10); the demo presents one canonical item per domain so the FHIR pair
 * is well-formed without committing to T-score conversion.
 */
const PROMIS29_QUESTIONS: ReadonlyArray<PromQuestionDef> = [
  {
    linkId: 'promis-29-q1',
    text: 'Physical Function: I have trouble doing my regular leisure activities',
    options: PROMIS29_LIKERT_5,
  },
  {
    linkId: 'promis-29-q2',
    text: 'Anxiety: I felt fearful in the past 7 days',
    options: PROMIS29_LIKERT_5,
  },
  {
    linkId: 'promis-29-q3',
    text: 'Depression: I felt worthless in the past 7 days',
    options: PROMIS29_LIKERT_5,
  },
  {
    linkId: 'promis-29-q4',
    text: 'Fatigue: I felt fatigued in the past 7 days',
    options: PROMIS29_LIKERT_5,
  },
  {
    linkId: 'promis-29-q5',
    text: 'Sleep Disturbance: My sleep quality was poor in the past 7 days',
    options: PROMIS29_LIKERT_5,
  },
  {
    linkId: 'promis-29-q6',
    text: 'Social Roles: I have trouble doing all the family activities expected of me',
    options: PROMIS29_LIKERT_5,
  },
  {
    linkId: 'promis-29-q7',
    text: 'Pain Interference: Pain interfered with my day-to-day activities',
    options: PROMIS29_LIKERT_5,
  },
];

const PROMIS29_BANDS: ReadonlyArray<PromScoreBand> = [
  { min: 7, max: 14, label: 'Within population norms' },
  { min: 15, max: 24, label: 'Mild to moderate symptom burden' },
  { min: 25, max: 35, label: 'High symptom burden' },
];

export const PROMIS29_DEFINITION: PromDefinition = {
  id: 'promis-29',
  title: 'PROMIS-29 Profile',
  description:
    'PROMIS-29 v2.1 health profile, one item per domain across the seven NIH PROMIS axes. Sum the 7 demo items, score 7 to 35.',
  questionnaireUrl: 'urn:cps:questionnaire:promis-29',
  loincCode: '82589-3',
  loincDisplay: 'PROMIS-29 v2.0 panel total raw score',
  scoreUnit: 'score',
  citation:
    'Cella D, Riley W, Stone A, et al. The Patient-Reported Outcomes Measurement Information System (PROMIS): progress of an NIH Roadmap cooperative group during its first two years. J Clin Epidemiol 2010;63(11):1179-1194. LOINC 82589-3.',
  advisoryFlag:
    'Demo bands map to a 7 to 35 sum; production scoring uses per-domain T-scores. Gap log: PROMIS-29-TSCORE-CONFIRM.',
  questions: PROMIS29_QUESTIONS,
  scoreBands: PROMIS29_BANDS,
  track: 'longevity',
  itemCountTotal: 29,
  licenseStatus: 'free',
};

// --- WHO-5 Well-Being Index -------------------------------------------------

const WHO5_LIKERT: ReadonlyArray<PromLikertOption> = [
  { label: 'At no time', value: 0 },
  { label: 'Some of the time', value: 1 },
  { label: 'Less than half', value: 2 },
  { label: 'More than half', value: 3 },
  { label: 'Most of the time', value: 4 },
  { label: 'All of the time', value: 5 },
];

const WHO5_QUESTIONS: ReadonlyArray<PromQuestionDef> = [
  {
    linkId: 'who-5-q1',
    text: 'In the past two weeks I have felt cheerful and in good spirits',
    options: WHO5_LIKERT,
  },
  {
    linkId: 'who-5-q2',
    text: 'In the past two weeks I have felt calm and relaxed',
    options: WHO5_LIKERT,
  },
  {
    linkId: 'who-5-q3',
    text: 'In the past two weeks I have felt active and vigorous',
    options: WHO5_LIKERT,
  },
  {
    linkId: 'who-5-q4',
    text: 'In the past two weeks I woke up feeling fresh and rested',
    options: WHO5_LIKERT,
  },
  {
    linkId: 'who-5-q5',
    text: 'In the past two weeks my daily life has been filled with things that interest me',
    options: WHO5_LIKERT,
  },
];

/**
 * WHO-5 raw-score bands per Topp et al (2015). The raw 0 to 25 sum is
 * conventionally multiplied by 4 to produce the 0 to 100 percentage; the
 * Demo card stores the raw sum and the band label captures the
 * percentage-converted threshold.
 */
const WHO5_BANDS: ReadonlyArray<PromScoreBand> = [
  { min: 0, max: 7, label: 'Poor well-being (raw 0 to 7, depression screen positive)' },
  { min: 8, max: 12, label: 'Reduced well-being (raw 8 to 12)' },
  { min: 13, max: 25, label: 'Adequate well-being (raw 13 to 25)' },
];

export const WHO5_DEFINITION: PromDefinition = {
  id: 'who-5',
  title: 'WHO-5 Well-Being',
  description:
    'WHO-5 Well-Being Index. 5 items, sum the raw scores 0 to 25; raw 12 or lower is the validated cutoff for further depression screening.',
  questionnaireUrl: 'urn:cps:questionnaire:who-5',
  loincCode: '89260-3',
  loincDisplay: 'WHO-5 well-being index total score',
  scoreUnit: 'score',
  citation:
    'Topp CW, Ostergaard SD, Sondergaard S, Bech P. The WHO-5 Well-Being Index: a systematic review of the literature. Psychother Psychosom 2015;84(3):167-176. LOINC 89260-3.',
  questions: WHO5_QUESTIONS,
  scoreBands: WHO5_BANDS,
  track: 'longevity',
  itemCountTotal: 5,
  licenseStatus: 'free',
};

// --- electronic Frailty Index (eFI) ----------------------------------------

const EFI_LIKERT: ReadonlyArray<PromLikertOption> = [
  { label: 'Not present', value: 0 },
  { label: 'Present', value: 1 },
];

/**
 * eFI demo subset captures 6 deficit indicators across the validated 36
 * deficit domain set (Clegg 2016). Each indicator is a binary 0 / 1; the
 * production index is computed as the proportion of present deficits
 * out of the 36-item battery (advisory flag explains the conversion).
 */
const EFI_QUESTIONS: ReadonlyArray<PromQuestionDef> = [
  {
    linkId: 'efi-q1',
    text: 'Difficulty walking 100 metres without assistance',
    options: EFI_LIKERT,
  },
  {
    linkId: 'efi-q2',
    text: 'Two or more falls in the past 12 months',
    options: EFI_LIKERT,
  },
  {
    linkId: 'efi-q3',
    text: 'Five or more chronic medications',
    options: EFI_LIKERT,
  },
  {
    linkId: 'efi-q4',
    text: 'Memory complaints in the past 12 months',
    options: EFI_LIKERT,
  },
  {
    linkId: 'efi-q5',
    text: 'Loss of more than 5 percent body weight in the past 12 months',
    options: EFI_LIKERT,
  },
  {
    linkId: 'efi-q6',
    text: 'Loss of energy or persistent fatigue',
    options: EFI_LIKERT,
  },
];

const EFI_BANDS: ReadonlyArray<PromScoreBand> = [
  { min: 0, max: 1, label: 'Fit (deficits 1 or fewer of 6)' },
  { min: 2, max: 3, label: 'Mild frailty (deficits 2 to 3 of 6)' },
  { min: 4, max: 6, label: 'Moderate to severe frailty (deficits 4 or more of 6)' },
];

export const EFI_DEFINITION: PromDefinition = {
  id: 'efi',
  title: 'eFI Frailty Screen',
  description:
    'Electronic Frailty Index demo subset. 6 binary deficit indicators across the validated 36-deficit battery. Sum, score 0 to 6.',
  questionnaireUrl: 'urn:cps:questionnaire:efi',
  loincCode: 'LA-CPS-EFI',
  loincDisplay: 'eFI demo subset deficit count (placeholder)',
  scoreUnit: 'deficits',
  citation:
    'Clegg A, Bates C, Young J, Ryan R, et al. Development and validation of an electronic frailty index using routine primary care electronic health record data. Age Ageing 2016;45(3):353-360.',
  advisoryFlag:
    'LOINC LA-CPS-EFI is a placeholder until clinical advisory confirms a canonical eFI LOINC. Production scoring is the proportion across the full 36-deficit battery. Tracked as eFI-LOINC-CONFIRM.',
  questions: EFI_QUESTIONS,
  scoreBands: EFI_BANDS,
  track: 'longevity',
  itemCountTotal: 36,
  licenseStatus: 'free',
};

// --- Healthy Aging Index ----------------------------------------------------

const HAI_LIKERT: ReadonlyArray<PromLikertOption> = [
  { label: 'Optimal', value: 0 },
  { label: 'Intermediate', value: 1 },
  { label: 'Impaired', value: 2 },
];

/**
 * Healthy Aging Index demo (Sanders et al 2014, J Gerontol). Five
 * physiologic systems each scored 0 / 1 / 2; a sixth Demo item adds a
 * patient-reported global aging perception so the card carries a self-
 * report alongside the physiologic systems.
 */
const HAI_QUESTIONS: ReadonlyArray<PromQuestionDef> = [
  {
    linkId: 'hai-q1',
    text: 'Cognitive function (Modified Mini-Mental State recall)',
    options: HAI_LIKERT,
  },
  {
    linkId: 'hai-q2',
    text: 'Pulmonary function (forced vital capacity vs predicted)',
    options: HAI_LIKERT,
  },
  {
    linkId: 'hai-q3',
    text: 'Renal function (cystatin C estimated GFR)',
    options: HAI_LIKERT,
  },
  {
    linkId: 'hai-q4',
    text: 'Glucose metabolism (fasting glucose category)',
    options: HAI_LIKERT,
  },
  {
    linkId: 'hai-q5',
    text: 'Vascular function (systolic blood pressure category)',
    options: HAI_LIKERT,
  },
  {
    linkId: 'hai-q6',
    text: 'Self-rated overall aging trajectory in the past year',
    options: HAI_LIKERT,
  },
];

const HAI_BANDS: ReadonlyArray<PromScoreBand> = [
  { min: 0, max: 3, label: 'Healthy aging trajectory' },
  { min: 4, max: 7, label: 'Intermediate aging trajectory' },
  { min: 8, max: 12, label: 'Accelerated aging trajectory' },
];

export const HAI_DEFINITION: PromDefinition = {
  id: 'healthy-aging-index',
  title: 'Healthy Aging Index',
  description:
    'Healthy Aging Index demo. 6 items across cognitive, pulmonary, renal, metabolic, vascular, and self-report dimensions. Sum, score 0 to 12.',
  questionnaireUrl: 'urn:cps:questionnaire:healthy-aging-index',
  loincCode: 'LA-CPS-HAI',
  loincDisplay: 'Healthy Aging Index demo total score (placeholder)',
  scoreUnit: 'score',
  citation:
    'Sanders JL, Boudreau RM, Cauley JA, et al. Association of a modified physiologic index with mortality and incident disability: the Health, Aging, and Body Composition study. J Gerontol A Biol Sci Med Sci 2014;69(2):187-194.',
  advisoryFlag:
    'LOINC LA-CPS-HAI is a placeholder until clinical advisory confirms a canonical Healthy Aging Index LOINC. Tracked as HAI-LOINC-CONFIRM.',
  questions: HAI_QUESTIONS,
  scoreBands: HAI_BANDS,
  track: 'longevity',
  itemCountTotal: 5,
  licenseStatus: 'free',
};

/** Canonical PROM definitions in display order (cardiometabolic first, then longevity). */
export const PROM_DEFINITIONS: ReadonlyArray<PromDefinition> = [
  PHQ9_DEFINITION,
  GAD7_DEFINITION,
  MSQ_DEFINITION,
  SF12_DEFINITION,
  GSRS_DEFINITION,
  IWQOL_DEFINITION,
  DQOL_DEFINITION,
  EQ5D_DEFINITION,
  KOOS_DEFINITION,
  PROMIS29_DEFINITION,
  WHO5_DEFINITION,
  EFI_DEFINITION,
  HAI_DEFINITION,
];

/** Map of PROM id to definition for fast lookup by selection callbacks. */
export const PROM_DEFINITION_BY_ID: Readonly<Record<PromId, PromDefinition>> = {
  'phq-9': PHQ9_DEFINITION,
  'gad-7': GAD7_DEFINITION,
  'msq-baseline': MSQ_DEFINITION,
  'sf-12': SF12_DEFINITION,
  gsrs: GSRS_DEFINITION,
  'iwqol-lite': IWQOL_DEFINITION,
  dqol: DQOL_DEFINITION,
  'eq-5d-5l': EQ5D_DEFINITION,
  'koos-12': KOOS_DEFINITION,
  'promis-29': PROMIS29_DEFINITION,
  'who-5': WHO5_DEFINITION,
  efi: EFI_DEFINITION,
  'healthy-aging-index': HAI_DEFINITION,
};

/**
 * Compute the sum-of-Likert score for a PROM submission. Throws when an
 * answer is missing or outside the option set so the caller never emits
 * a partial Observation.
 */
export function computePromScore(
  def: PromDefinition,
  answers: ReadonlyMap<string, number>,
): number {
  let sum = 0;
  for (const q of def.questions) {
    const v = answers.get(q.linkId);
    if (v === undefined) {
      throw new Error(
        `[prom-definitions] Missing answer for ${def.id} item ${q.linkId}`,
      );
    }
    const allowed = q.options.some((o) => o.value === v);
    if (!allowed) {
      throw new Error(
        `[prom-definitions] Answer ${v} for ${def.id} item ${q.linkId} not in option set`,
      );
    }
    sum += v;
  }
  return sum;
}

/**
 * Resolve the severity-band label for a score. Returns the verbatim band
 * label or `Out of range` if the score falls outside every band (which
 * should not happen for a clean submission).
 */
export function scoreBandFor(def: PromDefinition, score: number): string {
  for (const band of def.scoreBands) {
    if (score >= band.min && score <= band.max) return band.label;
  }
  return 'Out of range';
}
